import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { HoverHintDefinitionGrid, skuCatalogPeekRows } from "@/components/ui/HoverHintDefinitionGrid";
import { prefetchWorkOrderDetail } from "@/pages/manufacturing/workOrderDetailWorkspace";
import {
  fetchPurchaseInvoiceDetail,
  purchaseInvoiceDetailQueryKey,
} from "@/pages/mrp/purchaseInvoiceApi";
import { fetchPurchaseOrderDetail,
  purchaseOrderDetailQueryKey,
  PURCHASE_ORDER_DETAIL_STALE_MS,
} from "@/pages/mrp/purchaseOrderApi";
import { fetchSupplierDetail } from "@/lib/masterData";
import { fetchApInvoice } from "@/pages/accounting/apInvoicesApi";
import { fetchInboundReceiptReview } from "@/pages/shipping/inboundApi";

import {
  batchPeekQueryKey,
  devicePeekNeedsLookup,
  devicePeekQueryKey,
  fetchBatchPeekContext,
  fetchDevicePeekContext,
  fetchSalesOrderPeekContext,
  fetchShipmentPeekContext,
  salesOrderPeekQueryKey,
  shipmentPeekQueryKey,
} from "./entityPeekFetch";

import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { RepAgencyDetailHoverHint } from "@/components/inside-sales/RepAgencyDetailHoverHint";

import { GipiDetailHoverHint } from "./GipiDetailHoverHint";
import {
  InboundReceiptDetailHoverHint,
  inboundReceiptPeekQueryKey,
} from "./InboundReceiptDetailHoverHint";
import {
  VendorInvoiceDetailHoverHint,
  vendorInvoicePeekQueryKey,
} from "./VendorInvoiceDetailHoverHint";
import {
  BatchPeekHoverHintBody,
  CarrierPeekHoverHintBody,
  GosiPeekHoverHintBody,
  RcaPeekHoverHintBody,
  SalesOrderListPeekHoverHintBody,
  ShipmentPeekHoverHintBody,
  SkuQuantitiesPeekHoverHintBody,
  WorkOrderInlinePeekHoverHintBody,
} from "./entityPeekBodies";
import { PurchaseOrderDetailHoverHint } from "./PurchaseOrderDetailHoverHint";
import { SupplierDetailHoverHint, supplierDetailQueryKey } from "./SupplierDetailHoverHint";
import type { EntityPeekProps } from "./entityPeekTypes";
import { SalesOrderPeekHoverHintBody } from "./SalesOrderPeekHoverHintBody";
import {
  BatchDetailHoverHint,
  DeviceDetailHoverHint,
  SalesOrderDetailHoverHint,
  ShipmentDetailHoverHint,
} from "./EntityPeekByIdHoverHints";
import { WorkOrderDetailHoverHint } from "./WorkOrderDetailHoverHint";

/** Universal entity peek-through body. Pages pass `kind` + inline context or id — no local peek layouts. */
export function EntityPeekHint(props: EntityPeekProps): ReactNode {
  switch (props.kind) {
    case "sales_order":
      if (props.salesOrderId != null) {
        return (
          <SalesOrderDetailHoverHint
            salesOrderId={props.salesOrderId}
            context={props.context}
            emptyMessage={props.emptyMessage ?? "No sales order context."}
          />
        );
      }
      return (
        <SalesOrderPeekHoverHintBody
          context={props.context}
          emptyMessage={props.emptyMessage ?? "No sales order context."}
        />
      );
    case "sales_order_list":
      return (
        <SalesOrderListPeekHoverHintBody
          contexts={props.contexts}
          emptyMessage={props.emptyMessage ?? "No linked sales order context."}
        />
      );
    case "work_order":
      return <WorkOrderDetailHoverHint buildOrderId={props.buildOrderId} />;
    case "work_order_inline":
      return <WorkOrderInlinePeekHoverHintBody context={props.context} />;
    case "batch":
      if (props.batchId != null) {
        return (
          <BatchDetailHoverHint
            batchId={props.batchId}
            context={props.context}
            emptyMessage={props.emptyMessage ?? "Batch details unavailable."}
          />
        );
      }
      return (
        <BatchPeekHoverHintBody
          context={props.context}
          emptyMessage={props.emptyMessage ?? "Batch details unavailable."}
        />
      );
    case "shipment":
      if (props.shipmentId != null) {
        return (
          <ShipmentDetailHoverHint
            shipmentId={props.shipmentId}
            context={props.context}
            emptyMessage={props.emptyMessage ?? "No shipment details."}
          />
        );
      }
      return (
        <ShipmentPeekHoverHintBody
          context={props.context}
          emptyMessage={props.emptyMessage ?? "No shipment details."}
        />
      );
    case "purchase_order":
      return (
        <PurchaseOrderDetailHoverHint
          purchaseOrderId={props.purchaseOrderId}
          context={props.context}
          emptyMessage={props.emptyMessage ?? "No purchase order context."}
        />
      );
    case "supplier":
      return (
        <SupplierDetailHoverHint
          supplierId={props.supplierId}
          context={props.context}
          emptyMessage={props.emptyMessage ?? "No supplier context."}
        />
      );
    case "gosi":
      return <GosiPeekHoverHintBody context={props.context} />;
    case "device":
      return <DeviceDetailHoverHint context={props.context} />;
    case "rca":
      return <RcaPeekHoverHintBody context={props.context} />;
    case "gipi":
      return (
        <GipiDetailHoverHint
          purchaseInvoiceId={props.purchaseInvoiceId}
          vendorInvoiceNumber={props.vendorInvoiceNumber}
        />
      );
    case "vendor_invoice":
      return <VendorInvoiceDetailHoverHint invoiceId={props.invoiceId} />;
    case "inbound_receipt":
      return <InboundReceiptDetailHoverHint receiptId={props.receiptId} />;
    case "carrier":
      return <CarrierPeekHoverHintBody context={props.context} />;
    case "sku_catalog":
      return (
        <MrpSkuCatalogHoverHintBody sku={props.sku} hint={props.hint} loading={props.loading ?? false} />
      );
    case "sku_metadata":
      return <HoverHintDefinitionGrid rows={skuCatalogPeekRows(props.fields)} />;
    case "sku_quantities":
      return (
        <SkuQuantitiesPeekHoverHintBody
          lines={props.lines}
          emptyMessage={props.emptyMessage ?? "No line-level detail."}
        />
      );
    case "rep_agency":
      return <RepAgencyDetailHoverHint repAgencyId={props.repAgencyId} />;
    default: {
      const _exhaustive: never = props;
      return _exhaustive;
    }
  }
}

export function usePrefetchEntityPeek(props: EntityPeekProps | undefined): (() => void) | undefined {
  const queryClient = useQueryClient();
  if (props?.kind === "work_order") {
    return () => {
      void prefetchWorkOrderDetail(queryClient, props.buildOrderId);
    };
  }
  if (props?.kind === "gipi") {
    return () => {
      void queryClient.prefetchQuery({
        queryKey: purchaseInvoiceDetailQueryKey(props.purchaseInvoiceId),
        queryFn: () => fetchPurchaseInvoiceDetail(props.purchaseInvoiceId),
        staleTime: 30_000,
      });
    };
  }
  if (props?.kind === "purchase_order") {
    return () => {
      void queryClient.prefetchQuery({
        queryKey: purchaseOrderDetailQueryKey(props.purchaseOrderId),
        queryFn: () => fetchPurchaseOrderDetail(props.purchaseOrderId),
        staleTime: PURCHASE_ORDER_DETAIL_STALE_MS,
      });
    };
  }
  if (props?.kind === "supplier") {
    return () => {
      void queryClient.prefetchQuery({
        queryKey: supplierDetailQueryKey(props.supplierId),
        queryFn: () => fetchSupplierDetail(props.supplierId),
        staleTime: 30_000,
      });
    };
  }
  if (props?.kind === "batch" && props.batchId != null && props.context == null) {
    const batchId = props.batchId;
    return () => {
      void queryClient.prefetchQuery({
        queryKey: batchPeekQueryKey(batchId),
        queryFn: () => fetchBatchPeekContext(batchId),
        staleTime: 30_000,
      });
    };
  }
  if (props?.kind === "shipment" && props.shipmentId != null && props.context == null) {
    const shipmentId = props.shipmentId;
    return () => {
      void queryClient.prefetchQuery({
        queryKey: shipmentPeekQueryKey(shipmentId),
        queryFn: () => fetchShipmentPeekContext(shipmentId),
        staleTime: 30_000,
      });
    };
  }
  if (props?.kind === "vendor_invoice") {
    const invoiceId = props.invoiceId;
    return () => {
      void queryClient.prefetchQuery({
        queryKey: vendorInvoicePeekQueryKey(invoiceId),
        queryFn: () => fetchApInvoice(invoiceId),
        staleTime: 30_000,
      });
    };
  }
  if (props?.kind === "inbound_receipt") {
    const receiptId = props.receiptId;
    return () => {
      void queryClient.prefetchQuery({
        queryKey: inboundReceiptPeekQueryKey(receiptId),
        queryFn: () => fetchInboundReceiptReview(receiptId),
        staleTime: 30_000,
      });
    };
  }
  if (props?.kind === "sales_order" && props.salesOrderId != null && props.context == null) {
    const salesOrderId = props.salesOrderId;
    return () => {
      void queryClient.prefetchQuery({
        queryKey: salesOrderPeekQueryKey(salesOrderId),
        queryFn: () => fetchSalesOrderPeekContext(salesOrderId),
        staleTime: 30_000,
      });
    };
  }
  if (props?.kind === "device" && devicePeekNeedsLookup(props.context)) {
    const serial = props.context.serial;
    return () => {
      void queryClient.prefetchQuery({
        queryKey: devicePeekQueryKey(serial),
        queryFn: () => fetchDevicePeekContext(serial),
        staleTime: 30_000,
      });
    };
  }
  return undefined;
}

export function useEntityPeekPrefetchHandlers(
  peek: EntityPeekProps | undefined,
): { onMouseEnter?: () => void; onFocus?: () => void } {
  const prefetch = usePrefetchEntityPeek(peek);
  if (prefetch == null) {
    return {};
  }
  return { onMouseEnter: prefetch, onFocus: prefetch };
}
